import React, { useEffect, useMemo, useState } from 'react';
import { onValue, ref, set } from 'firebase/database';
import { database } from '../firebaseConfig';
import WORLD_CUP_FIXTURE from '../data/worldCupFixture';
import './WorldCupResults.css';

const FIXTURE_PATH = 'fixture-2026/matches';

const STATUS_OPTIONS = [
    { value: 'scheduled', label: 'Programado' },
    { value: 'live', label: 'En vivo' },
    { value: 'halftime', label: 'Entretiempo' },
    { value: 'finished', label: 'Finalizado' },
    { value: 'postponed', label: 'Postergado' },
    { value: 'cancelled', label: 'Cancelado' }
];

function getFirebaseErrorMessage(error, action) {
    if (error?.code === 'PERMISSION_DENIED' || error?.code === 'database/permission-denied') {
        return `Firebase no permite ${action}. Revisa las reglas de fixture-2026/matches.`;
    }

    return `No se pudo ${action}. Intenta nuevamente.`;
}

function WorldCupResults({ canEdit }) {
    const [matches, setMatches] = useState(WORLD_CUP_FIXTURE);
    const [selectedId, setSelectedId] = useState(1);
    const [homeScore, setHomeScore] = useState('');
    const [awayScore, setAwayScore] = useState('');
    const [status, setStatus] = useState('scheduled');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        const fixtureRef = ref(database, FIXTURE_PATH);

        return onValue(
            fixtureRef,
            (snapshot) => {
                const remoteMatches = snapshot.val() || {};
                const mergedMatches = WORLD_CUP_FIXTURE.map((fallbackMatch) => ({
                    ...fallbackMatch,
                    ...(remoteMatches[fallbackMatch.id] || {})
                }));

                setMatches(mergedMatches);
                setLoading(false);
            },
            (error) => {
                console.error('Error al leer el fixture:', error);
                setMessage({
                    type: 'error',
                    text: getFirebaseErrorMessage(error, 'leer los partidos')
                });
                setLoading(false);
            }
        );
    }, []);

    const selectedMatch = useMemo(
        () => matches.find((match) => Number(match.id) === Number(selectedId)) || WORLD_CUP_FIXTURE[0],
        [matches, selectedId]
    );

    useEffect(() => {
        const scoreParts = String(selectedMatch.result || '').match(/^\s*(\d+)\s*-\s*(\d+)\s*$/);
        setHomeScore(scoreParts?.[1] || '');
        setAwayScore(scoreParts?.[2] || '');
        setStatus(selectedMatch.status || 'scheduled');
        setMessage(null);
    }, [selectedMatch]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setMessage(null);

        if (!canEdit) {
            setMessage({ type: 'error', text: 'Tu usuario no tiene permiso para editar resultados.' });
            return;
        }

        const hasHomeScore = homeScore !== '';
        const hasAwayScore = awayScore !== '';

        if (hasHomeScore !== hasAwayScore) {
            setMessage({ type: 'error', text: 'Ingresa ambos marcadores o deja los dos vacios.' });
            return;
        }

        if (['live', 'halftime', 'finished'].includes(status) && !hasHomeScore) {
            setMessage({ type: 'error', text: 'Este estado necesita el marcador de ambos equipos.' });
            return;
        }

        const parsedHomeScore = hasHomeScore ? Number(homeScore) : null;
        const parsedAwayScore = hasAwayScore ? Number(awayScore) : null;

        if (hasHomeScore && (
            !Number.isInteger(parsedHomeScore) ||
            !Number.isInteger(parsedAwayScore) ||
            parsedHomeScore < 0 ||
            parsedAwayScore < 0 ||
            parsedHomeScore > 99 ||
            parsedAwayScore > 99
        )) {
            setMessage({ type: 'error', text: 'Los goles deben ser numeros enteros entre 0 y 99.' });
            return;
        }

        setSaving(true);

        try {
            await set(ref(database, `${FIXTURE_PATH}/${selectedMatch.id}`), {
                id: Number(selectedMatch.id),
                result: hasHomeScore ? `${parsedHomeScore} - ${parsedAwayScore}` : '',
                status
            });

            setMessage({ type: 'success', text: 'Resultado actualizado correctamente.' });
        } catch (error) {
            console.error('Error al actualizar el resultado:', error);
            setMessage({
                type: 'error',
                text: getFirebaseErrorMessage(error, 'guardar el resultado')
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="world-cup-page">
            <header className="world-cup-header">
                <div>
                    <p className="world-cup-eyebrow">Mundial 2026</p>
                    <h1>Resultados de partidos</h1>
                    <p>Actualiza el marcador que se muestra en las paginas conectadas.</p>
                </div>
                <span className="world-cup-count">72 partidos</span>
            </header>

            <div className="world-cup-layout">
                <section className="world-cup-editor" aria-labelledby="result-editor-title">
                    <div className="world-cup-section-heading">
                        <div>
                            <h2 id="result-editor-title">Editar resultado</h2>
                            <p>Los cambios se publican en tiempo real.</p>
                        </div>
                        <span className={`match-status status-${status}`}>
                            {STATUS_OPTIONS.find((option) => option.value === status)?.label || 'Programado'}
                        </span>
                    </div>

                    <label className="field-label" htmlFor="match-selector">Partido</label>
                    <select
                        id="match-selector"
                        className="match-selector"
                        value={selectedId}
                        onChange={(event) => setSelectedId(Number(event.target.value))}
                        disabled={loading || saving}
                    >
                        {matches.map((match) => (
                            <option key={match.id} value={match.id}>
                                #{match.id} - {match.homeTeam} vs {match.awayTeam}
                            </option>
                        ))}
                    </select>

                    <div className="match-information">
                        <div>
                            <span>Fecha y hora</span>
                            <strong>{selectedMatch.dateTime}</strong>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="scoreboard">
                            <label>
                                <span>{selectedMatch.homeTeam}</span>
                                <input
                                    type="number"
                                    min="0"
                                    max="99"
                                    step="1"
                                    inputMode="numeric"
                                    value={homeScore}
                                    onChange={(event) => setHomeScore(event.target.value)}
                                    disabled={!canEdit || saving}
                                    aria-label={`Goles de ${selectedMatch.homeTeam}`}
                                />
                            </label>
                            <span className="score-separator">-</span>
                            <label>
                                <span>{selectedMatch.awayTeam}</span>
                                <input
                                    type="number"
                                    min="0"
                                    max="99"
                                    step="1"
                                    inputMode="numeric"
                                    value={awayScore}
                                    onChange={(event) => setAwayScore(event.target.value)}
                                    disabled={!canEdit || saving}
                                    aria-label={`Goles de ${selectedMatch.awayTeam}`}
                                />
                            </label>
                        </div>

                        <div className="result-fields single-field">
                            <label>
                                <span className="field-label">Estado</span>
                                <select
                                    value={status}
                                    onChange={(event) => setStatus(event.target.value)}
                                    disabled={!canEdit || saving}
                                >
                                    {STATUS_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        {message && (
                            <div className={`result-message ${message.type}`} role="status">
                                {message.text}
                            </div>
                        )}

                        <button className="save-result-button" type="submit" disabled={!canEdit || saving || loading}>
                            {saving ? 'Guardando...' : 'Guardar resultado'}
                        </button>
                    </form>
                </section>

                <section className="world-cup-list" aria-labelledby="matches-title">
                    <div className="world-cup-section-heading">
                        <div>
                            <h2 id="matches-title">Partidos</h2>
                            <p>Selecciona uno para editarlo.</p>
                        </div>
                    </div>

                    <div className="matches-scroll">
                        {matches.map((match) => (
                            <button
                                type="button"
                                key={match.id}
                                className={`match-row ${Number(selectedId) === Number(match.id) ? 'selected' : ''}`}
                                onClick={() => setSelectedId(Number(match.id))}
                            >
                                <span className="match-number">{match.id}</span>
                                <span className="match-teams">
                                    <strong>{match.homeTeam} vs {match.awayTeam}</strong>
                                    <small>{match.dateTime}</small>
                                </span>
                                <span className="match-score">
                                    {match.result || '-'}
                                </span>
                            </button>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
}

export default WorldCupResults;
